/**
 * CruisePortCallsPanel.tsx — クルーズ船の寄港予定の一覧・公開／非公開（/cruise の「寄港予定」タブ）（2026-10-31・2026-11-01 自動公開）
 *
 * 沖縄県オープンデータから取り込んだ予定は自動で公開し、LP の「寄港スケジュールで選ぶ」に出る。時刻が変わった予定は新しい時刻で公開、
 * 最新の予定に載らなくなった予定は自動で非公開。スタッフが非公開にした予定は次の取り込みでも非公開のまま（前田さん 2026-11-01）。
 * 確かめる日（初期値: 毎週月曜日。更新日が変わっていれば取り込む）の設定・今すぐ取り込む・手入力の追加もここ。
 * 正: booking-platform/md/pricing.md「クルーズ船の予約 — 2026-10-31 の決定」 / ページ仕様: md/master_md/page-cruise.md
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Button, TextField, MenuItem, Alert, Checkbox, Chip,
  Table, TableHead, TableRow, TableCell, TableBody, ToggleButton, ToggleButtonGroup, Dialog, DialogTitle,
  DialogContent, DialogActions, Link,
} from '@mui/material';
import dayjs from 'dayjs';
import { logEvent } from '../../lib/eventLog';
import { CRUISE_PLANS } from '../../lib/cruisePrices';
import {
  fetchPortCallsAdmin, filterCalls, needsReview, pendingLabel, parseDays, savePortCall, savePortCallSettings,
  setPortCallsStatus, settingsLabel, statusLabel, timesLabel, importPortCallsNow, deletePortCall, isSourceStale, WEEKDAY_OPTIONS,
  type ImportMode, type PortCall, type PortCallFilter, type PortCallInput, type PortCallsAdmin,
} from '../../lib/cruisePortCalls';

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];
const dayLabel = (d: string) => `${dayjs(d).format('YYYY/M/D')}（${WEEKDAYS[dayjs(d).day()]}）`;
const RESULT_LABEL: Record<string, string> = {
  ok: '取り込みました', error: '失敗', unchanged: '更新なし（取り込まず）', skipped: '確かめる日ではない',
};

const emptyInput = (): PortCallInput => ({ date: '', ship: '', arrive: '08:00', depart_date: '', depart: '18:00', status: 'published' });

const CruisePortCallsPanel: React.FC<{ store: string }> = ({ store }) => {
  const [data, setData] = useState<PortCallsAdmin | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [filter, setFilter] = useState<PortCallFilter>('published');
  const [selected, setSelected] = useState<number[]>([]);
  const [settingsDraft, setSettingsDraft] = useState<{ mode: ImportMode; weekday: number; days: string } | null>(null);
  const [editing, setEditing] = useState<PortCallInput | null>(null);

  const from = dayjs().format('YYYY-MM-DD');
  const to = dayjs().add(365, 'day').format('YYYY-MM-DD');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await fetchPortCallsAdmin(store, from, to);
      setData(d);
      setSettingsDraft({ mode: d.import.settings.mode, weekday: d.import.settings.weekdays[0] ?? 1, days: d.import.settings.days.join(', ') });
      setSelected([]);
    } catch (e) {
      setError(`読み込みに失敗しました: ${(e as Error).message}`);
      logEvent({ action: 'cruise_port_load', target: store, result: 'fail', err: e });
    } finally {
      setLoading(false);
    }
  }, [store, from, to]);

  useEffect(() => { load(); }, [load]);

  const calls = useMemo(() => data?.calls || [], [data]);
  const shown = useMemo(() => filterCalls(calls, filter), [calls, filter]);
  const reviewCount = calls.filter(needsReview).length;

  const run = async (label: string, action: string, fn: () => Promise<string>) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const msg = await fn();
      logEvent({ action, target: store, result: 'ok' });
      setNotice(msg);
      await load();
    } catch (e) {
      setError(`${label}に失敗しました: ${(e as Error).message}`);
      logEvent({ action, target: store, result: 'fail', err: e });
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = (ids: number[], action: 'publish' | 'hide') => run(
    action === 'publish' ? '公開' : '非公開', `cruise_port_${action}`,
    async () => `${await setPortCallsStatus(store, ids, action)} 件を${action === 'publish' ? '公開しました（お客様の画面に出ます）' : '非公開にしました'}`,
  );

  const saveSettings = () => {
    if (!settingsDraft) return;
    const days = parseDays(settingsDraft.days);
    if (settingsDraft.mode === 'monthly' && !days) { setError('日は 1〜28 の数字で入れてください（例: 10 または 10, 20）'); return; }
    run('設定の保存', 'cruise_port_settings', async () => {
      const s = await savePortCallSettings(store, {
        mode: settingsDraft.mode, weekdays: [settingsDraft.weekday], days: days || data?.import.settings.days || [10],
      });
      return `「${settingsLabel(s)}」にしました`;
    });
  };

  const importNow = () => run('取り込み', 'cruise_port_import', async () => {
    const r = await importPortCallsNow(store);
    const c = r.counts?.[store];
    const head = r.stale ? '取り込みました（ただし CSV の更新日が長く変わっていません）' : '取り込みました';
    if (!c) return head;
    // 2026-11-01 自動公開: 新しい・下書きだった・また載った予定は公開、載らなくなった予定は非公開にした
    const published = (c.new ?? 0) + (c.from_draft ?? 0) + (c.restored ?? 0);
    return `${head}: 公開 ${published} 件（うち新しい ${c.new ?? 0} 件）・時刻の変更 ${c.changed ?? 0} 件・載らなくなって非公開 ${(c.missing ?? 0) + (c.missing_draft ?? 0)} 件`;
  });

  const saveEdit = () => {
    if (!editing) return;
    const input = { ...editing, depart_date: editing.depart_date || editing.date };
    run('保存', 'cruise_port_save', async () => {
      await savePortCall(store, input);
      setEditing(null);
      return '保存しました';
    });
  };

  const removeManual = (c: PortCall) => {
    if (!window.confirm(`${dayLabel(c.date)} ${c.ship} を消しますか？`)) return;
    run('削除', 'cruise_port_delete', async () => { await deletePortCall(store, c.id); return '消しました'; });
  };

  const toggle = (id: number) => setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  const allShownSelected = shown.length > 0 && shown.every(c => selected.includes(c.id));
  const imp = data?.import;

  return (
    <Box>
      <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
        沖縄県オープンデータから取り込んだ寄港予定です。取り込んだ予定は<b>自動で公開</b>され、お客様の予約フォーム（寄港スケジュールで選ぶ）に出ます。
        時刻が変わった予定は新しい時刻で公開し、最新の予定に載らなくなった予定は自動で非公開にします（どちらも店舗のメールでお知らせ）。
        ここで「非公開」にした予定は、次の取り込みでも非公開のままです。
      </Typography>

      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 260 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>前回の取り込み</Typography>
          <Typography variant="body2" sx={{ color: imp?.last_result === 'error' ? '#dc2626' : '#475569' }}>
            {imp?.last_run_at ? `${dayjs(imp.last_run_at).format('M/D HH:mm')} ${RESULT_LABEL[imp.last_result || ''] || imp.last_result || ''}` : 'まだ取り込んでいません'}
          </Typography>
          {imp?.last_error && <Typography variant="caption" sx={{ color: '#dc2626', display: 'block' }}>{imp.last_error}</Typography>}
          {imp && isSourceStale(imp) && (
            <Typography variant="caption" sx={{ color: '#b45309', display: 'block', fontWeight: 700 }}>
              CSV の更新日が {imp.stale_days} 日変わっていません（{imp.stale_threshold_days} 日以上でメールでお知らせ）。NACRU 等で最新を確かめてください
            </Typography>
          )}
          {imp?.last_source_name && (
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
              {imp.last_source_name}（更新日 {imp.last_source_modified ? dayjs(imp.last_source_modified).format('YYYY/M/D') : '不明'}）
            </Typography>
          )}
        </Box>
        {settingsDraft && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField select size="small" label="更新を確かめる日" value={settingsDraft.mode} sx={{ minWidth: 180 }}
              onChange={e => setSettingsDraft(s => (s ? { ...s, mode: e.target.value as ImportMode } : s))}>
              <MenuItem value="weekly">毎週</MenuItem>
              <MenuItem value="monthly">毎月決まった日</MenuItem>
              <MenuItem value="daily">毎日</MenuItem>
            </TextField>
            {settingsDraft.mode === 'weekly' && (
              <TextField select size="small" label="曜日" value={settingsDraft.weekday} sx={{ width: 110 }}
                onChange={e => setSettingsDraft(s => (s ? { ...s, weekday: Number(e.target.value) } : s))}>
                {WEEKDAY_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            )}
            {settingsDraft.mode === 'monthly' && (
              <TextField size="small" label="日（例: 10）" value={settingsDraft.days} sx={{ width: 120 }}
                onChange={e => setSettingsDraft(s => (s ? { ...s, days: e.target.value } : s))} />
            )}
            <Button variant="outlined" size="small" onClick={saveSettings} disabled={busy}>設定を保存</Button>
          </Box>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" onClick={() => setEditing(emptyInput())} disabled={busy}>手入力で追加</Button>
        <Button variant="contained" onClick={importNow} disabled={busy || loading}>{busy ? '処理中…' : '今すぐ取り込む'}</Button>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
        <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, v) => { if (v) { setFilter(v); setSelected([]); } }}>
          <ToggleButton value="review">要確認（{reviewCount}）</ToggleButton>
          <ToggleButton value="published">公開中</ToggleButton>
          <ToggleButton value="hidden">非公開</ToggleButton>
          <ToggleButton value="all">すべて</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" variant="contained" color="success" disabled={busy || selected.length === 0} onClick={() => changeStatus(selected, 'publish')}>
          選んだ {selected.length} 件を公開
        </Button>
        <Button size="small" variant="outlined" color="inherit" disabled={busy || selected.length === 0} onClick={() => changeStatus(selected, 'hide')}>
          非公開に
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <Paper sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 820 }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox checked={allShownSelected} indeterminate={!allShownSelected && shown.some(c => selected.includes(c.id))}
                    onChange={() => setSelected(allShownSelected ? [] : shown.map(c => c.id))} inputProps={{ 'aria-label': 'すべて選ぶ' }} />
                </TableCell>
                <TableCell>入港日</TableCell>
                <TableCell>船名</TableCell>
                <TableCell>時刻</TableCell>
                <TableCell>バース・前港→次港</TableCell>
                <TableCell>状態</TableCell>
                <TableCell align="right">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.length === 0 && (
                <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', color: '#64748b', py: 4 }}>
                  {filter === 'review' ? '確かめてほしい予定はありません' : '予定はありません'}
                </TableCell></TableRow>
              )}
              {shown.map(c => {
                const pend = pendingLabel(c);
                const review = needsReview(c);
                return (
                  <TableRow key={c.id} hover selected={selected.includes(c.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={selected.includes(c.id)} onChange={() => toggle(c.id)} inputProps={{ 'aria-label': `${c.date} ${c.ship} を選ぶ` }} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{dayLabel(c.date)}</TableCell>
                    <TableCell>
                      {c.ship}
                      {c.source === 'manual' && <Chip size="small" label="手入力" sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <span style={{ textDecoration: pend ? 'line-through' : 'none', color: pend ? '#94a3b8' : undefined }}>{timesLabel(c)}</span>
                      {pend && <Typography variant="body2" sx={{ color: '#b45309', fontWeight: 700 }}>新: {pend}</Typography>}
                      {c.plan && (
                        <Typography variant="caption" sx={{ display: 'block', color: '#0369a1' }}>
                          プラン {CRUISE_PLANS.find(p => p.key === c.plan)?.label}・貸出 {c.plan_pickup}／返却 {c.plan_return}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ color: '#64748b', fontSize: 12 }}>
                      {[c.berth, c.prev_port || c.next_port ? `${c.prev_port || '—'}→${c.next_port || '—'}` : null].filter(Boolean).join('・') || '—'}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={statusLabel(c)}
                        color={c.missing_in_source ? 'error' : review ? 'warning' : c.status === 'published' ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {(c.status !== 'published' || c.pending) && (
                        <Button size="small" color="success" disabled={busy} onClick={() => changeStatus([c.id], 'publish')}>
                          {c.pending ? '新しい時刻で公開' : '公開'}
                        </Button>
                      )}
                      {(c.status !== 'hidden' || review) && (
                        <Button size="small" color="inherit" disabled={busy} onClick={() => changeStatus([c.id], 'hide')}
                          title="次の取り込みでも公開し直しません">
                          {c.status === 'hidden' ? '非公開のままにする' : '非公開'}
                        </Button>
                      )}
                      <Button size="small" disabled={busy} onClick={() => setEditing({
                        id: c.id, date: c.date, ship: c.ship, arrive: c.arrive, depart_date: c.depart_date, depart: c.depart,
                        status: c.status === 'draft' ? 'published' : c.status,
                      })}>時刻を直す</Button>
                      {c.source === 'manual' && <Button size="small" color="error" disabled={busy} onClick={() => removeManual(c)}>消す</Button>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      {data?.source && (
        <Typography variant="caption" sx={{ display: 'block', color: '#64748b', mt: 1 }}>
          出典: <Link href={data.source.url} target="_blank" rel="noopener noreferrer">{data.source.label}</Link>
          。最新の予定は NACRU（那覇港管理組合）や港の発表でも確かめてください。
        </Typography>
      )}

      <Dialog open={editing !== null} onClose={() => setEditing(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing?.id ? '時刻を直す' : '寄港予定を手入力で追加'}</DialogTitle>
        {editing && (
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
            <TextField type="date" size="small" label="入港日" value={editing.date} InputLabelProps={{ shrink: true }}
              onChange={e => setEditing({ ...editing, date: e.target.value })} />
            <TextField size="small" label="船名" value={editing.ship} onChange={e => setEditing({ ...editing, ship: e.target.value })} />
            <TextField type="time" size="small" label="入港" value={editing.arrive} InputLabelProps={{ shrink: true }}
              onChange={e => setEditing({ ...editing, arrive: e.target.value })} />
            <TextField type="date" size="small" label="出港日（同じ日なら空欄）" value={editing.depart_date} InputLabelProps={{ shrink: true }}
              onChange={e => setEditing({ ...editing, depart_date: e.target.value })} />
            <TextField type="time" size="small" label="出港" value={editing.depart} InputLabelProps={{ shrink: true }}
              onChange={e => setEditing({ ...editing, depart: e.target.value })} />
            <TextField select size="small" label="公開" value={editing.status}
              onChange={e => setEditing({ ...editing, status: e.target.value as PortCallInput['status'] })}>
              <MenuItem value="published">公開する</MenuItem>
              <MenuItem value="hidden">非公開</MenuItem>
            </TextField>
            {editing.id && <Typography variant="caption" sx={{ color: '#64748b' }}>取り込んだ予定は日付・船名を変えられません（次の取り込みで別の予定として入るため）。</Typography>}
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setEditing(null)}>やめる</Button>
          <Button variant="contained" onClick={saveEdit} disabled={busy || !editing?.date || !editing?.ship.trim()}>保存</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CruisePortCallsPanel;
